import React from 'react';
import { Helmet } from 'react-helmet-async';
import { absoluteUrl, organizationSchema, SITE_URL, socialImageUrl } from '@/lib/seo';

interface SEOProps {

  title: string;
  description: string;
  keywords?: string;
  imageUrl?: string;
  url?: string;
  robots?: string;
  suppressImagePreview?: boolean;
  ogType?: 'website' | 'article';
  schema?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  imageUrl,
  url,
  robots,
  suppressImagePreview,
  ogType = 'website',
  schema,
}) => {
  const siteName = 'CanXP AI';
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const inferredUrl = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
  const fullUrl = absoluteUrl(url || inferredUrl);
  const fullImageUrl = !suppressImagePreview ? socialImageUrl(imageUrl) : undefined;
  const schemas = [organizationSchema, ...(Array.isArray(schema) ? schema : schema ? [schema] : [])];

  return (

    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={robots || 'index,follow'} />
      <link rel="canonical" href={fullUrl} />

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={siteName} />
      {fullImageUrl ? <meta property="og:image" content={fullImageUrl} /> : null}
      {fullImageUrl ? <meta property="og:image:secure_url" content={fullImageUrl} /> : null}
      {fullImageUrl ? <meta property="og:image:url" content={fullImageUrl} /> : null}
      {fullImageUrl ? <meta property="og:image:alt" content={fullTitle} /> : null}
      {fullImageUrl ? <meta property="og:image:type" content="image/png" /> : null}
      {fullImageUrl ? <meta property="og:image:width" content="1200" /> : null}
      {fullImageUrl ? <meta property="og:image:height" content="630" /> : null}

      <meta name="twitter:card" content={fullImageUrl ? 'summary_large_image' : 'summary'} />

      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {fullImageUrl ? <meta name="twitter:image" content={fullImageUrl} /> : null}
      {fullImageUrl ? <meta name="twitter:image:alt" content={fullTitle} /> : null}
      <meta name="twitter:site" content="@canxpai" />

      {schemas.map((item, index) => (
        <script key={`${SITE_URL}-schema-${index}`} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
